import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {environment} from 'environments/environment';
import {UserAPIService} from '@app/shared/api/user.api.service';
import {Globals} from '@app/shared/globals/globals';
import {IState} from '@app/models/state/state.model';
import {CommonMessages} from '@app/constants/common.messages';
import {AuthMessages} from '@app/auth/auth.messages';
import {NotifyUtils} from '@app/shared/utils/notify.utils';
import {TranslateService} from '@ngx-translate/core';
import {UserState} from '@app/models/user/user.model';
import { PasswordReset } from '@app/models/user/password-reset.model';
import { HttpErrorResponse } from '@angular/common/http';
import { FrankliValidators } from '@app/shared/validators/validators';

interface PageState extends IState {
  submitted: boolean,
  submitting: boolean
}

const NEW_USER_STATES = [UserState.INVITED, UserState.PENDING];

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit {
  public readonly eCommonMessages = CommonMessages;
  public readonly eAuthMessages = AuthMessages;

  state: PageState;

  form: FormGroup;
  private passwordToken : string;
  private passwordReset = false;

  minimumPasswordLength = environment.password.minimumLength;
  maximumPasswordLength = environment.password.maximumLength;
  header: string;
  subheader: string;
  button: string;

  get controlPassword(): FormControl {
    return this.form.controls.password as FormControl;
  }

  get controlConfirmPassword(): FormControl {
    return this.form.controls.confirmPassword as FormControl;
  }

  get isNewUser(): boolean {
    if (!this.globals.user) {
      return true;
    }
    if (!this.globals.user.userState) {
      return true;
    }

    return NEW_USER_STATES.includes(this.globals.user.userState);
  }

  constructor(
      public route: ActivatedRoute,
      private userAPIService: UserAPIService,
      private translateService: TranslateService,
      private notifyUtils: NotifyUtils,
      private router: Router,
      public globals: Globals, 
  ) {
    this.state = {
      loading: true,
      error: false,
      errorMessage: '',
      submitted: false,
      submitting: false
    };

    this.form = this.initForm();
  }

  ngOnInit() {
    if(this.globals.user){
      this.passwordReset = this.globals.user.passwordReset;
      this.setWording();
      this.state.loading = false;
    } else{
      this.passwordToken = this.route.snapshot.paramMap.get('token');
    
      this.userAPIService.checkPasswordToken(this.passwordToken).subscribe(result => {
        if(!result.valid){
          this.handleError('The link you are trying to use has expired. Please try again.');
        }
        this.setWording();
        this.state.loading = false;
      }, 
      (error: HttpErrorResponse) => {
        if(this.passwordReset){
          this.handleError(error.error);
        }
      });    
    }
    
    
  }



  setWording(){
    this.header = this.passwordReset? this.translateService.instant(AuthMessages.FIRST_TIME_LOGIN) : this.translateService.instant(AuthMessages.CHANGE_PASSWORD);
    this.subheader = this.passwordReset? this.translateService.instant(AuthMessages.FIRST_TIME_LOGIN_DESCRIPTION) : this.translateService.instant(AuthMessages.CHANGE_PASSWORD_DESCRIPTION);
    this.button = this.passwordReset? this.translateService.instant(AuthMessages.SET_PASSWORD) : this.translateService.instant(AuthMessages.CHANGE_PASSWORD);
  }

  initForm(): FormGroup {
    const formGroup = new FormGroup({
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(environment.password.minimumLength),
        Validators.maxLength(environment.password.maximumLength), 
        FrankliValidators.passwordValidator
      ]),
      confirmPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(environment.password.minimumLength),
        Validators.maxLength(environment.password.maximumLength), 
        FrankliValidators.passwordValidator
      ]),
    }, [
      this.validatorPasswordsMatch()
    ]);

    return formGroup;
  }

  validatorPasswordsMatch(): ValidatorFn {
    return (form: FormGroup): (ValidationErrors | null) => {
      if (!form.controls.password) {
        return null;
      }
      if (!form.controls.confirmPassword) {
        return null;
      }

      const valuePassword = form.controls.password.value;
      const valueConfirm = form.controls.confirmPassword.value;

      if (!valuePassword) {
        return null;
      }
      if (!valueConfirm) {
        return null;
      }

      if (valueConfirm === valuePassword) {
        return null;
      }

      return {
        passwordmatch: true
      };
    };
  }

  resetForm(): void {
    this.state = {
      loading: false,
      error: false,
      errorMessage: '',
      submitted: false,
      submitting: false
    };

    this.form = this.initForm();
  }

  formSubmit() {
    this.state.submitted = true;

    if (this.form.invalid) {
      return;
    }

    this.state.submitting = true;
    const passwordReset = {password: this.controlPassword.value, passwordToken: this.passwordToken} as PasswordReset;

    const call = this.passwordReset ? this.userAPIService.setPassword(passwordReset) : this.userAPIService.updatePassword(passwordReset);
    call.subscribe(
      (res) => {
        const messageKey = this.passwordReset ? AuthMessages.SUCCESS_CREATE_PASSWORD : AuthMessages.SUCCESS_CHANGE_PASSWORD;
        this.notifyUtils.notify(messageKey);
        this.resetForm();
         
        if(this.passwordReset){
          this.userAPIService.getUserInit().subscribe(res => {
            this.router.navigate(['/dashboard']);
          });
        } else{
          this.router.navigate(['/auth/login']);
        }
          
      },
      () => {
        this.state.submitting = false;
      }
    );
  }

  private handleError(message: string) {
    localStorage.setItem('sso_error', `${Date.now()}: ${message}`);
    this.router.navigateByUrl('/');
  }
}
